import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FullCalendar, {
  type CalendarRef,
  type DateClickInfo,
  type DatesSetInfo,
  type EventClickInfo,
} from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/react/daygrid";
import interactionPlugin from "@fullcalendar/react/interaction";
import classicThemePlugin from "calendar/_views/full-calendar-theme";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled, type Theme } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useGetCalendarEventsQuery,
  type CalendarEventRange,
} from "calendar/_api/calendar-event-api";
import {
  calendarEventColors,
  calendarEventLegend,
  toFullCalendarEvent,
} from "calendar/calendar-event";
import {
  calendarEventTimeFormat,
  clickableCalendarEventClass,
  clickableCalendarEventStyles,
} from "calendar/_views/calendar-event-presentation";
import {
  CalendarEventPreviewPopper,
  useCalendarEventPreview,
} from "calendar/_views/components/calendar-event-preview";
import CalendarDayView from "calendar/_views/components/calendar-day-view";
import CalendarEventDetails from "calendar/_views/components/calendar-event-details";
import CalendarEventEditor from "calendar/_views/components/calendar-event-editor";
import type { CalendarEventOccurrenceTime, CalendarEventResponse } from "calendar/_schemas/calendar-event-schema";
import { calendarRoute } from "calendar/calendar-navigation";
import { localDateInputLength, toLocalDateInput } from "_shared/dates/date-input";
import { useSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useLocation, useNavigate } from "react-router";

const PageContainer = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  height: "100%",
}));
// The title stands as far below the app bar as a proposal page's does: about 16px. On a phone the
// container's padding went to zero for the grid's sake and took the top with it, so the heading sat 1px
// under the app bar; the top keeps its padding at every width, and only the sides give it up.
const CalendarContainer = styled("div")(({ theme }) => ({
  boxSizing: "border-box",
  height: "100%",
  margin: "auto",
  [theme.breakpoints.down("xl")]: {
    padding: 0,
    paddingTop: theme.spacing(2),
  },
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(1),
    paddingTop: theme.spacing(1.5),
  },
}));
// On a phone the container drops its padding so the month grid can use every pixel of the width,
// and that left the month heading, the navigation, the New event button and the legend touching both
// edges of the screen. They take the 16px inset other pages give their title row instead; only
// the grid stays flush. A margin rather than a padding, because on an `Alert` a padding would
// only widen the alert's own inside.
const phoneInset = (theme: Theme) => ({
  [theme.breakpoints.down("sm")]: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
});
const CalendarLayout = styled(Box)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  // Everything above the body: the header and whichever loading or error notice is showing.
  "& > :not(:last-child)": phoneInset(theme),
}));
// The month is the page's heading: the side navigation already says this is the calendar, so the
// row spends its width on the month, the navigation and New event. The legend explains the grid's
// colours and sits under it. From `md` the row reads month, alert, navigation, New event; below it
// the four no longer fit one row, so New event stays at the top right beside the month and the
// alert and the navigation take the rows below.
const CalendarHeader = styled(Box)(({ theme }) => ({ minHeight: 48, display: "flex", alignItems: "center", gap: theme.spacing(1), flexWrap: "wrap" }));
// MUI pads the icon (22px) and the message (a 20px line) separately and anchors both to the top, so
// trimming each to 4px left the icon box 2px taller than the text's and the icon sat 1px low. One
// padding on the alert and centred children put the icon's middle on the line's middle.
const NoEventsAlert = styled(Alert)(({ theme }) => ({
  alignItems: "center",
  paddingTop: 4,
  paddingBottom: 4,
  "& .MuiAlert-icon, & .MuiAlert-message": { paddingTop: 0, paddingBottom: 0 },
  [theme.breakpoints.down("md")]: { order: 2, flexBasis: "100%" },
}));
const CalendarNavigation = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  [theme.breakpoints.up("md")]: { marginLeft: "auto" },
  [theme.breakpoints.down("md")]: { order: 3 },
}));
const NewEventButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down("md")]: { order: 1, marginLeft: "auto" },
}));
const CalendarBody = styled(Box)(({ theme }) => ({ minHeight: 0, flex: 1, display: "flex", flexDirection: "column", gap: theme.spacing(1) }));
const CalendarGrid = styled(Box)({ minHeight: 0, flex: 1, ...clickableCalendarEventStyles });
const EventLegend = styled(Box)(({ theme }) => ({ display: "flex", flexDirection: "row", gap: theme.spacing(1), flexWrap: "wrap", ...phoneInset(theme) }));
const EventLegendItem = styled(Box)(({ theme }) => ({ display: "flex", flexDirection: "row", gap: theme.spacing(0.5), alignItems: "center" }));
const EventLegendDot = styled(Box, { shouldForwardProp: (prop) => prop !== "dotColor" })<{ dotColor: string }>(({ dotColor }) => ({ width: 12, height: 12, borderRadius: "50%", backgroundColor: dotColor }));
const calendarWheelNavigationCooldownMs = 100;

export default function Calendar() {
  const { closeDialog, openDialog, popDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationParameters = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialDate = navigationParameters.get("date") ?? undefined;
  const initialEventId = navigationParameters.get("eventId");
  const initialOccurrence = useMemo<CalendarEventOccurrenceTime | undefined>(() => {
    const start = navigationParameters.get("occurrenceStart");
    if (!start) return undefined;
    return {
      start,
      end: navigationParameters.get("occurrenceEnd"),
      isAllDay: navigationParameters.get("occurrenceAllDay") === "true",
      timeZoneId: null,
    };
  }, [navigationParameters]);
  const calendarRef = useRef<CalendarRef>(null);
  const calendarGridRef = useRef<HTMLDivElement>(null);
  const wheelNavigationLocked = useRef(false);
  const wheelNavigationUnlockTimer = useRef<number | undefined>(undefined);
  const openedInitialEventId = useRef<string | null>(null);
  const [calendarTitle, setCalendarTitle] = useState(() => new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric",
  }).format(new Date()));
  // Wait for FullCalendar's authoritative rendered range to avoid requesting a speculative initial month.
  const [visibleRange, setVisibleRange] = useState<CalendarEventRange>();
  const eventsQuery = useGetCalendarEventsQuery(visibleRange ?? skipToken);
  const eventPreview = useCalendarEventPreview();
  const closeEventPreview = eventPreview.close;
  const calendarEvents = useMemo(
    () => (eventsQuery.data ?? []).map(toFullCalendarEvent),
    [eventsQuery.data],
  );
  const onDatesSet = useCallback((range: DatesSetInfo) => {
    const nextRange = { from: range.start.toISOString(), to: range.end.toISOString() };
    closeEventPreview();
    setCalendarTitle(range.view.title);
    setVisibleRange((current) => current?.from === nextRange.from && current?.to === nextRange.to ? current : nextRange);
  }, [closeEventPreview]);
  const clearNavigationTarget = useCallback(() => {
    if (location.search) navigate(calendarRoute, { replace: true });
  }, [location.search, navigate]);
  const openEventEditor = useCallback((defaultDate: string, event?: CalendarEventResponse) => {
    clearNavigationTarget();
    openDialog({
      title: event ? event.recurrence ? "Edit Recurring Series" : "Edit Event" : "New Event",
      component: (
        <CalendarEventEditor
          defaultDate={defaultDate}
          event={event}
          onCancel={popDialog}
          onSaved={(mode) => {
            closeDialog();
            enqueueSnackbar(mode === "created" ? "Calendar event created." : "Calendar event updated.", { variant: "success" });
          }}
        />
      ),
      MuiProps: { fullWidth: true, maxWidth: "md" },
    });
  }, [clearNavigationTarget, closeDialog, enqueueSnackbar, openDialog, popDialog]);
  const openEventDetails = useCallback((
    eventId: string,
    occurrence?: CalendarEventOccurrenceTime,
    preserveNavigationTarget = false,
  ) => {
    if (!preserveNavigationTarget) clearNavigationTarget();
    openDialog({
      title: "Event details",
      component: <CalendarEventDetails eventId={eventId} occurrence={occurrence} onEdit={(event) => openEventEditor(toLocalDateInput(new Date()), event)} />,
      onClose: clearNavigationTarget,
      MuiProps: { fullWidth: true, maxWidth: "sm" },
    });
  }, [clearNavigationTarget, openDialog, openEventEditor]);
  useEffect(() => {
    if (!initialEventId || openedInitialEventId.current === initialEventId) return;
    openedInitialEventId.current = initialEventId;
    openEventDetails(initialEventId, initialOccurrence, true);
  }, [initialEventId, initialOccurrence, openEventDetails]);
  const onEventClick = useCallback(({ event, jsEvent }: EventClickInfo) => {
    jsEvent.preventDefault();
    closeEventPreview();
    const eventId = event.extendedProps.calendarEventId;
    if (typeof eventId === "string") {
      openEventDetails(eventId, {
        start: event.startStr,
        end: event.endStr || null,
        isAllDay: event.allDay,
        timeZoneId: null,
      });
    }
  }, [closeEventPreview, openEventDetails]);
  const onDateClick = useCallback(({ dateStr }: DateClickInfo) => {
    clearNavigationTarget();
    const date = dateStr.slice(0, localDateInputLength);
    openDialog({
      title: new Intl.DateTimeFormat(undefined, { dateStyle: "full" }).format(new Date(`${date}T00:00:00`)),
      component: (
        <CalendarDayView
          date={date}
          events={calendarEvents}
          onEventClick={openEventDetails}
          onNewEvent={() => openEventEditor(date)}
          canCreateEvent={date >= toLocalDateInput(new Date())}
        />
      ),
      MuiProps: { fullWidth: true, maxWidth: "xl" },
      contentSxProps: { overflow: "hidden" },
    });
  }, [calendarEvents, clearNavigationTarget, openDialog, openEventDetails, openEventEditor]);
  const onCalendarWheel = useCallback((event: WheelEvent) => {
    if (event.ctrlKey || event.deltaY === 0) return;

    event.preventDefault();
    // A mouse wheel or trackpad emits a burst of events. Keep extending the quiet period so one burst
    // produces one month change while separate gestures remain responsive.
    if (wheelNavigationUnlockTimer.current !== undefined)
      window.clearTimeout(wheelNavigationUnlockTimer.current);
    wheelNavigationUnlockTimer.current = window.setTimeout(() => {
      wheelNavigationLocked.current = false;
      wheelNavigationUnlockTimer.current = undefined;
    }, calendarWheelNavigationCooldownMs);
    if (wheelNavigationLocked.current) return;

    wheelNavigationLocked.current = true;
    clearNavigationTarget();
    if (event.deltaY > 0)
      calendarRef.current?.getApi().next();
    else
      calendarRef.current?.getApi().prev();
  }, [clearNavigationTarget]);
  useEffect(() => {
    const calendarGrid = calendarGridRef.current;
    if (!calendarGrid) return;

    // React's passive wheel listener cannot prevent page scrolling while the wheel navigates months.
    calendarGrid.addEventListener("wheel", onCalendarWheel, { passive: false });
    return () => calendarGrid.removeEventListener("wheel", onCalendarWheel);
  }, [onCalendarWheel]);
  useEffect(() => () => {
    if (wheelNavigationUnlockTimer.current !== undefined)
      window.clearTimeout(wheelNavigationUnlockTimer.current);
  }, []);
  const navigateCalendar = useCallback((direction: "next" | "previous" | "today") => {
    clearNavigationTarget();
    const calendar = calendarRef.current?.getApi();
    if (direction === "next") calendar?.next();
    else if (direction === "previous") calendar?.prev();
    else calendar?.today();
  }, [clearNavigationTarget]);

  return (
    <PageContainer>
      <CalendarContainer>
        <CalendarLayout>
          <CalendarHeader>
            <Typography variant="h5" component="h1">{calendarTitle}</Typography>
            {!eventsQuery.isUninitialized && !eventsQuery.isLoading && !eventsQuery.isError && calendarEvents.length === 0 && (
              <NoEventsAlert severity="info">
                No events are scheduled for this month.
              </NoEventsAlert>
            )}
            <CalendarNavigation>
              <Button size="small" onClick={() => navigateCalendar("today")}>Today</Button>
              <Tooltip title="Previous month">
                <IconButton aria-label="Previous month" size="small" onClick={() => navigateCalendar("previous")}>
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Next month">
                <IconButton aria-label="Next month" size="small" onClick={() => navigateCalendar("next")}>
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>
            </CalendarNavigation>
            <NewEventButton variant="contained" onClick={() => openEventEditor(toLocalDateInput(new Date()))}>
              New event
            </NewEventButton>
          </CalendarHeader>
          {eventsQuery.isLoading && (
            <Alert icon={<CircularProgress size={18} />} severity="info">Loading calendar events…</Alert>
          )}
          {eventsQuery.isFetching && !eventsQuery.isLoading && (
            <Typography role="status" variant="caption" color="text.secondary">Refreshing calendar events…</Typography>
          )}
          {eventsQuery.isError && (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => void eventsQuery.refetch()}>Retry</Button>}>
              Calendar events could not be loaded.
            </Alert>
          )}
          <CalendarBody>
            <CalendarGrid ref={calendarGridRef}>
              <FullCalendar
                ref={calendarRef}
                headerToolbar={false}
                height="100%"
                plugins={[dayGridPlugin, interactionPlugin, classicThemePlugin]}
                initialDate={initialDate}
                initialView="dayGridMonth"
                timeZone="local"
                events={calendarEvents}
                dateClick={onDateClick}
                eventClass={clickableCalendarEventClass}
                eventClick={onEventClick}
                eventTimeFormat={calendarEventTimeFormat}
                datesSet={onDatesSet}
                eventDisplay="block"
                {...eventPreview.fullCalendarProps}
              />
              <CalendarEventPreviewPopper shown={eventPreview.shown} previewId={eventPreview.previewId} />
            </CalendarGrid>
            <EventLegend aria-label="Event type legend">
              {calendarEventLegend.map(({ kind, label }) => (
                <EventLegendItem key={kind}>
                  <EventLegendDot aria-hidden="true" dotColor={calendarEventColors[kind]} />
                  <Typography variant="caption">{label}</Typography>
                </EventLegendItem>
              ))}
            </EventLegend>
          </CalendarBody>
        </CalendarLayout>
      </CalendarContainer>
    </PageContainer>
  );
}
